import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { renderTemplate, unresolvedPlaceholders } from './threadUtils';
import { smsSegments, formatPhone } from './threadUtils';

const MAX_CHARS = 1600;

/**
 * Reply box with quick-action chips (booking link, templates, document request
 * when wired), AI personalise, and a live SMS segment counter.
 * `ref.insert(text)` lets the rail drop text into the draft.
 */
const MessageComposer = forwardRef(function MessageComposer({
  onSend, onPersonalize, personalizing = false, disabled = false,
  templates = [], templateContext = {}, bookingLink = null, senderPhone = null, patientFirstName = '',
  onRequestDocument = null, onSendDocuments = null, sendBlockedReason = null, blocked = false, onResendNotice = null,
}, ref) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [menu, setMenu] = useState(null); // 'templates' | null
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 140) + 'px';
    }
  }, [message]);

  const insert = (text) => {
    if (!text) return;
    setMessage(prev => (prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? prev + ' ' : prev) + text);
    setMenu(null);
    textareaRef.current?.focus();
  };
  // replace() swaps the whole draft (an edited AI draft); insert() appends (booking link, template).
  const replace = (text) => { setMessage(text || ''); setTimeout(() => textareaRef.current?.focus(), 0); };
  useImperativeHandle(ref, () => ({ insert, replace, focus: () => textareaRef.current?.focus() }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    // Same rule as the Send button: Enter must not bypass the blocked, over-length or unfilled-field checks.
    if (!canSend) return;
    try {
      setSending(true);
      await onSend(message);
      setMessage('');
    } finally {
      setSending(false);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
    if (e.key === 'Escape') setMenu(null);
  };

  const seg = smsSegments(message);
  const over = message.length > MAX_CHARS;
  const segClass = over ? 'over' : seg.segments > 1 ? 'warn' : '';
  // A template field the thread could not fill must never reach the patient as raw braces.
  const unresolved = unresolvedPlaceholders(message);
  const canSend = !!message.trim() && !sending && !disabled && !over && !blocked && unresolved.length === 0;
  const placeholder = blocked
    ? `Texting is blocked: ${sendBlockedReason || 'this patient opted out'}`
    : sendBlockedReason
      ? `Reply to ${patientFirstName || 'patient'}… ${sendBlockedReason}`
      : `Reply to ${patientFirstName || 'patient'}…`;

  return (
    <form onSubmit={handleSubmit}>
      {blocked && (
        <div className="nh-blocked">⛔ {sendBlockedReason || 'This patient texted STOP.'} The carrier would reject the text. Call them, or wait for them to text START.</div>
      )}
      {unresolved.length > 0 && (
        <div className="nh-blocked warn">✎ Fill in {unresolved.map(k => `{{${k}}}`).join(', ')} before sending. The thread could not supply {unresolved.length === 1 ? 'this field' : 'these fields'}.</div>
      )}
      <div className="nh-chips">
        {onRequestDocument && (
          <button type="button" className="nh-chip doc" onClick={onRequestDocument} disabled={disabled}>📎 Request a document</button>
        )}
        {onSendDocuments && (
          <button type="button" className="nh-chip doc" onClick={onSendDocuments} disabled={disabled} title="Send a PDF or image over a secure link">📤 Send a file</button>
        )}
        {bookingLink && (
          <button type="button" className="nh-chip cal" onClick={() => insert(bookingLink)} disabled={disabled} title={bookingLink}>📅 Booking link</button>
        )}
        {templates.length > 0 && (
          <button type="button" className="nh-chip tpl" onClick={() => setMenu(m => m === 'templates' ? null : 'templates')} disabled={disabled} aria-expanded={menu === 'templates'}>≡ Templates</button>
        )}
        {onPersonalize && (
          <button type="button" className="nh-chip ai" onClick={() => onPersonalize(message)} disabled={!message.trim() || personalizing || disabled} title="Rewrite this draft for the patient with AI">
            {personalizing ? '… personalising' : '✦ AI personalise'}
          </button>
        )}
        {onResendNotice && (
          <button type="button" className="nh-chip notice" onClick={onResendNotice} disabled={disabled || blocked} title="Text the patient that SMS is not fully secure and documents go over the secure link">🔒 Resend privacy notice</button>
        )}
      </div>

      {menu === 'templates' && (
        <div className="nh-menu" role="menu">
          {templates.map(t => (
            <button key={t.id} type="button" role="menuitem" className="mi" onClick={() => insert(renderTemplate(t.content, templateContext))}>
              <b>{t.template_name || t.name}</b>
              <small>{t.content}</small>
            </button>
          ))}
          {templates.length === 0 && <div className="none">No SMS templates for this practice yet.</div>}
        </div>
      )}

      <div className={`nh-box${over ? ' over' : ''}`}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || sending || blocked}
          aria-label="Message"
        />
        <div className="nh-tools">
          <button type="submit" className="send" disabled={!canSend} title="Send">
            {sending ? <Spinner /> : <SendIcon />}
          </button>
        </div>
      </div>

      <div className="nh-foot">
        <span>
          <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> new line
          {senderPhone && <> · sends from <span>{formatPhone(senderPhone)}</span></>}
        </span>
        <span className={`seg ${segClass}`} title={`${seg.encoding} encoding`}>
          {over
            ? `${message.length.toLocaleString()} / ${MAX_CHARS.toLocaleString()} · too long`
            : `${seg.chars} chars · ${seg.segments} SMS segment${seg.segments === 1 ? '' : 's'}`}
        </span>
      </div>
    </form>
  );
});

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'nh-spin 1s linear infinite' }} aria-hidden="true">
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

export default MessageComposer;
